import React, { useEffect, useMemo, useState } from "react";
import useBaseRequest from "../../api/BaseRequest";
import DocumentGateway from "../../api/gateways/DocumentGateway";
import { TableRow } from "../../components/table";
import { DocumentStatus } from "../../entities/IDocument";
import { useUser } from "../../store/auth/AuthSelectors";
import { documentTableHeaders as allDocumentTableHeaders } from "./Documents.types";
import environment from "../../config/environment";
import { IDocumentTableColumns } from "../../config/environment/environment";
import { useNavigate } from "react-router";
import { screens } from "../Navigation.types";
import { useDocumentService } from "../../services/useDocumentService";
import { useLocation } from "react-router-dom";
import { documentFilters as documentDefaultFilters } from "./Documents.types";
import { mapDocumentToTableRow } from "./utils/mapDocumentToTableRow";

const colKeyMap: Record<string, keyof IDocumentTableColumns | undefined> = {
  "metaData.username": "userName",
  "metaData.description": "description",
  "metaData.dateCreated": "creationDate",
  "metaData.dateArchived": "archiveDate",
  "documentType": "documentType",
  "metaData.documentStatus": "documentStatus",
  "tags": "tags",
};

const cols = environment.documentTableColumns;
const allHeaderKeys = allDocumentTableHeaders.map((h) => h.key);
const filteredHeaders = allDocumentTableHeaders.filter((h) => {
  const colKey = colKeyMap[h.key];
  return colKey === undefined || cols[colKey];
});

export function useDocuments() {
  const user = useUser();
  const navigate = useNavigate();
  const { getDocFileUrl, useCreateLocalFileUrl } = useDocumentService();
  const location = useLocation();
  const { useGetDocumentCategories, useGetDocumentTypes } = useDocumentService();
  const { documentCategories } = useGetDocumentCategories();
  const { documentTypes } = useGetDocumentTypes();

  // states
  const [docTableData, setDocTableData] = React.useState<TableRow[]>([]);
  const [selectedDocuments, setSelectedDocuments] = React.useState<number[]>([]);
  const [showArchiveBookModal, setShowArchiveBookModal] = React.useState(false);

  const userDocumentsFilters = {
    name: "status",
    operator: "eq",
    value: DocumentStatus.PENDING,
    type: "string",
  };

  // requests
  const { data, execute, loading } = useBaseRequest(DocumentGateway.getDocuments, {
    onCompleted: (data) => {
      const { documents } = data;
      const docTable: TableRow[] = [];
      documents.forEach((doc) => {
        const fullRow = mapDocumentToTableRow(doc, setDownloadUrl);
        const filteredRow = filteredHeaders.map((h) => fullRow[allHeaderKeys.indexOf(h.key)]);
        docTable.push(filteredRow);
      });
      setDocTableData(docTable);
    },
    onError: (error) => {
      console.error("Error loading documents:", error);
      setDocTableData([]);
    },
  });

  const [downloadUrl, setDownloadUrl] = useState<string>("");
  const { url } = useCreateLocalFileUrl(downloadUrl, true);

  useEffect(() => {
    execute((location.search || "?"));
  }, [location.search]);

  // handlers
  const onRowPress = (id: number) => {
    navigate(screens.documents + `/${id}`);
  };

  const onArchiveBookItemCreate = () => {
    setShowArchiveBookModal(false);
    setSelectedDocuments([]);
  };

  // filters
  const documentFilters = useMemo(
    () => ({
      ...documentDefaultFilters,
      docTypeId: {
        ...documentDefaultFilters.docTypeId,
        options:
          documentTypes && documentTypes.length
            ? documentTypes.map((type) => {
              return {
                value: type.id.toString(),
                label: type.name,
              };
            })
            : [],
      },
      "metaData.documentCategory": {
        ...documentDefaultFilters["metaData.documentCategory"],
        options:
          documentCategories && documentCategories.length
            ? documentCategories.map((category) => {
              return {
                value: category.name,
                label: category.name,
              };
            })
            : [],
      },
      "metaData.documentStatus": {
        ...documentDefaultFilters["metaData.documentStatus"],
        options: [
          {
            "value": "Signed",
            "label": "Signed"
          },
          {
            "value": "Sealed",
            "label": "Sealed"
          },
          {
            "value": "Signed & Sealed",
            "label": "Signed & Sealed"
          },
          {
            "value": "Renewed",
            "label": "Renewed"
          },
          {
            "value": "Destroyed",
            "label": "Destroyed"
          },
          {
            "value": "Archived",
            "label": "Archived"
          },
          {
            "value": "New",
            "label": "New"
          },
          {
            "value": "Installer Error",
            "label": "Installer Error"
          },
          {
            "value": "Sales Error",
            "label": "Sales Error"
          }
        ]
      },
      tags: {
        ...documentDefaultFilters.tags,
        options:
          user?.tags && user.tags.length
            ? user.tags.map((tag) => ({
                value: tag,
                label: tag,
              }))
            : [],
      },
    }),
    [documentCategories, documentTypes, user?.tags]
  );

  return {
    docTableData,
    count: data?.count || 0,
    getDocuments: execute,
    loading,
    user,
    selectedDocuments,
    setSelectedDocuments,
    showArchiveBookModal,
    openArchiveBookModal: () => setShowArchiveBookModal(true),
    closeArchiveBookModal: () => setShowArchiveBookModal(false),
    onArchiveBookItemCreate,
    documentTableHeaders: filteredHeaders,
    onRowPress,
    documentFilters,
    documentCategories,
  };
}
